import { memo, useEffect, useMemo, useState } from "react";
import { type AddressData } from "../Address";
import { Trans, useTranslation } from "react-i18next";
import { CreateEditAddressModal } from "./components/CreateEditAddressModal";
import { deleteAddress } from "~/features/cart/cart/api/deleteAddress";
import { editAddress } from "~/features/cart/cart/api/editAddress";
import { createAddress } from "~/features/cart/cart/api/createAddress";
import { createAddressLine1 } from "~/utils/addressUtils";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogHeader,
  DialogTitle,
  InputAdornment,
  Radio,
  Stack,
  styled,
  TextField,
  Typography,
} from "~/common/mui";
import { C_ON_SURFACE_VARIANT, C_OUTLINE } from "~/common/components/icon/Colors";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";

interface Props {
  activeCartId: string;
  addresses: AddressData[];
  selectedAddress: AddressData;
  updateAddress: (selected: AddressData, temporary?: boolean) => void;
  customerNumber: number | string;
  countriesList: {
    isocode: string;
    name: string;
  }[];
  canAddAddress: boolean;
  onClose: () => void;
}

const AddressDropdown = (props: Props) => {
  const {
    activeCartId,
    addresses,
    updateAddress,
    customerNumber,
    countriesList,
    canAddAddress,
    selectedAddress,
    onClose,
  } = props;
  const { t } = useTranslation();
  const [selectedAddressId, setSelectedAddressId] = useState<string>(selectedAddress?.id ?? "");
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [addressChosenToEdit, setAddressChosenToEdit] = useState<AddressData | null>(null);
  const [sortedAddresses, setSortedAddresses] = useState<AddressData[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");

  useEffect(() => {
    sortAddressesAndSetState(addresses);
  }, [addresses]);

  const sortAddressesAndSetState = (addressesForSort: AddressData[], newSelectedId?: string) => {
    let defaultAddress: AddressData | undefined;
    const approvedAddress: AddressData[] = [];
    const editableAddress: AddressData[] = [];

    //manage putting to the right array
    addressesForSort.forEach((address, index) => {
      if (address.default) {
        defaultAddress = address;
      } else if (address.editable) {
        editableAddress.push(address);
      } else {
        approvedAddress.push(address);
      }
    });

    //manage sorting alphabeticly
    approvedAddress.sort((a, b) => (a.companyName + a.lines[0]).localeCompare(b.companyName + b.lines[0]));
    editableAddress.sort((a, b) => (a.companyName + a.lines[0]).localeCompare(b.companyName + b.lines[0]));

    //put in right order
    const sortedArray: AddressData[] = defaultAddress
      ? [defaultAddress, ...approvedAddress, ...editableAddress]
      : [...approvedAddress, ...editableAddress];

    setSortedAddresses(sortedArray);

    const candidateId = newSelectedId ?? selectedAddressId ?? selectedAddress.id ?? sortedArray[0]?.id ?? "";
    const exists = sortedArray.some((addr) => addr.id === candidateId);
    setSelectedAddressId(exists ? candidateId : "");

    if (newSelectedId && exists) {
      const addr = sortedArray.find((a) => a.id === newSelectedId)!;
      updateAddress(addr, addr.temporary);
    }
  };
  const selectAddress = (id: string) => {
    const addr = filteredAddresses.find((a) => a.id === id);
    if (!addr) {
      return;
    }
    if (addr.editable) {
      updateAddress(addr, addr.temporary);
    } else {
      updateAddress(addr);
    }
    onClose();
  };

  const deleteAdressOnFront = (addressId: string) => {
    setSortedAddresses((prev) => {
      const next = prev.filter((address) => address.id !== addressId);
      if (selectedAddressId === addressId) {
        const fallback = next[0];
        setSelectedAddressId(fallback?.id ?? "");
        if (fallback) {
          updateAddress(fallback, fallback.temporary);
        }
      }
      return next;
    });
  };

  const editAdressOnFront = (addressId: string, newAddress: AddressData) => {
    const newAddressList = [...sortedAddresses];
    const editingAddressIndex = newAddressList.findIndex((address) => address.id === addressId);
    newAddressList[editingAddressIndex] = newAddress;
    sortAddressesAndSetState(newAddressList);
    if (addressId === selectedAddressId) {
      updateAddress(newAddress, newAddress.temporary);
    }
  };

  const addAdressOnFront = (newAddress: AddressData) => {
    const newAddressList = [...sortedAddresses];
    newAddressList.push(newAddress);
    sortAddressesAndSetState(newAddressList, newAddress.id);
  };

  const renderAddressesLabel = (isDefault: boolean, isEditable: boolean) => {
    return isDefault
      ? `${t("checkoutPage.addressDropdown.default")} ${customerNumber}`
      : isEditable
      ? t("checkoutPage.addressDropdown.editable")
      : t("checkoutPage.addressDropdown.approved");
  };

  const getAddressLine1 = (addressLines: string[]) => {
    if (addressLines.length > 2) {
      return <div>{addressLines[0] + " " + addressLines[1]}</div>;
    }
    return <div>{addressLines[0]}</div>;
  };

  const getAddressLine2 = (addressLines: string[]) => {
    if (addressLines.length > 2) {
      return <div>{addressLines[2]}</div>;
    }
    return addressLines[1] ? <div>{addressLines[1]}</div> : null;
  };

  // Search Method
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value;
    setSearchTerm(term);
  };

  const filteredAddresses = useMemo(() => {
    const lowered = searchTerm.toLowerCase();
    if (!lowered) {
      return sortedAddresses;
    }
    return sortedAddresses.filter((addr) => {
      if (addr.default) {
        return false;
      }
      const line1 = (addr.lines?.[0] ?? "").toLowerCase();
      const line2 = (addr.lines?.[1] ?? "").toLowerCase();
      const company = (addr.companyName ?? "").toLowerCase();
      const zip = (addr.postalCode ?? "").toLowerCase();
      const countryTranslated = t(addr.country?.name ?? "").toLowerCase();
      const countryRaw = (addr.country?.name ?? "").toLowerCase();
      return (
        company.includes(lowered) ||
        line1.includes(lowered) ||
        line2.includes(lowered) ||
        zip.includes(lowered) ||
        countryTranslated.includes(lowered) ||
        countryRaw.includes(lowered)
      );
    });
  }, [sortedAddresses, searchTerm, t]);
  const selectionVisible = !!selectedAddressId && filteredAddresses.some((a) => a.id === selectedAddressId);
  const defaulAddress = sortedAddresses[0];

  const footer = () => (
    <FooterStack>
      {canAddAddress && (
        <Button
          variant="text"
          startIcon={<AddIcon />}
          onClick={(e) => {
            e.stopPropagation();
            setIsDropdownOpen(false);
            setIsModalOpen(true);
            setAddressChosenToEdit(null);
          }}
        >
          {t("checkoutPage.addressDropdown.addNew")}
        </Button>
      )}

      <Stack direction="row" gap={1} sx={{ ml: "auto" }}>
        <Button variant="text" onClick={onClose}>
          {t("generic.back")}
        </Button>
        <Button onClick={() => selectAddress(selectedAddressId)} disabled={!selectionVisible}>
          {t("selectAddressPopup.title")}
        </Button>
      </Stack>
    </FooterStack>
  );

  const renderAddressCard = (address: AddressData, index: number) => (
    <Stack
      direction={"row"}
      sx={{
        padding: "8px 16px",
        justifyContent: "space-between",
        alignItems: "flex-start",
        border: `1px solid ${C_OUTLINE}`,
        borderRadius: "12px",
      }}
    >
      <Radio
        checked={selectionVisible && address.id === selectedAddressId}
        onChange={() => {
          setSelectedAddressId(address.id);
        }}
        value="a"
        name="radio-buttons"
        inputProps={{ "aria-label": "A" }}
      />
      <div
        style={{
          width: "100%",
        }}
      >
        <Typography variant="bodyLarge">{address.companyName}</Typography>
        <Typography>
          {getAddressLine1(address.lines)}
          {getAddressLine2(address.lines)}
        </Typography>
        <Typography>{t(address.country.name)}</Typography>
      </div>
      {address.editable && (
        <Button
          variant="text"
          data-test-id={"checkout-dropdown-address-btn-edit-item"}
          onClick={(e) => {
            e.stopPropagation();
            setIsDropdownOpen(false);
            setIsModalOpen(true);
            setAddressChosenToEdit(address);
          }}
          startIcon={<EditIcon />}
        >
          {t("page.linear.summary.btn.edit")}
        </Button>
      )}
      {address.default && (
        <Typography
          variant="labelSmall"
          sx={{
            minWidth: "148px",
          }}
        >
          {t("checkoutPage.address.dialog.defaultAddress")}
        </Typography>
      )}
    </Stack>
  );

  const renderAddressList = (addresses: AddressData[]) => {
    let lastCategory = "";

    function getCategory(address: AddressData) {
      if (address.default) {
        return "default";
      }
      if (address.editable) {
        return "editable";
      }
      return "approved";
    }

    return (
      <>
        {addresses.map((address: AddressData, index: number) => {
          const category = getCategory(address);
          const showTitle = category !== lastCategory;
          lastCategory = category;

          return (
            <div key={address.id}>
              {showTitle && !address.default && (
                <Typography variant="bodyMedium" paddingBottom={1}>
                  {renderAddressesLabel(address.default, address.editable)}
                </Typography>
              )}
              {renderAddressCard(address, index)}
            </div>
          );
        })}
      </>
    );
  };

  return (
    <Dialog open={true} maxWidth="desktop-s">
      <DialogHeader onClose={onClose}>
        <DialogTitle>{t("checkoutPage.address.dialog.selectAddress")}</DialogTitle>
      </DialogHeader>
      <DialogContent>
        <Stack gap={1}>
          <TextField
            placeholder={t("shipping.address.search.field.placeholder")}
            onChange={handleChange}
            value={searchTerm}
            variant="outlined"
            fullWidth
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              },
            }}
          />
          {filteredAddresses.length === 0 ? (
            <Typography variant="bodyMedium" color={C_ON_SURFACE_VARIANT}>
              <Trans i18nKey={"shipping.address.no.result.found.text"} searchTerm={searchTerm}>
                <b>{{ searchTerm: searchTerm }}</b>
              </Trans>
            </Typography>
          ) : null}

          <Stack sx={{ minHeight: "587px" }} gap={1}>
            {renderAddressList(filteredAddresses)}
          </Stack>
          {isModalOpen && (
            <CreateEditAddressModal
              onClose={() => {
                setIsModalOpen(false);
              }}
              address={addressChosenToEdit}
              countriesList={countriesList}
              onDelete={(address) => {
                if (selectedAddressId === address.id && defaulAddress) {
                  updateAddress(defaulAddress, defaulAddress.temporary);
                }
                deleteAddress(activeCartId, {}, address.id, address.temporary);
                deleteAdressOnFront(address.id);
                setIsModalOpen(false);
              }}
              onSave={async (address) => {
                const isNew = !address.id;

                const body = {
                  company: address.companyName,
                  houseNumber: address.houseNumber,
                  street: address.street,
                  addressTwo: address.addressAdditional,
                  zipCode: address.zipCode,
                  city: address.city,
                  country: address.country,
                  savable: address.persistForLater,
                };

                const country = countriesList.find((country) => country.isocode === address.country)!;

                const lines = [createAddressLine1(address.houseNumber, address.street)];
                if (address.addressAdditional) {
                  lines.push(address.addressAdditional);
                }
                lines.push([address.zipCode, address.city].join(" "));

                const addressData: AddressData = {
                  id: address.id ?? "",
                  companyName: address.companyName,
                  lines,
                  postalCode: address.zipCode,
                  town: address.city,
                  country,
                  temporary: !address.persistForLater,
                  default: false,
                  editable: true,
                  state: "",
                };

                if (isNew) {
                  const res = await createAddress(activeCartId, body);
                  const addressId = await res.text();
                  addressData.id = addressId;
                  addAdressOnFront(addressData);
                  setIsModalOpen(false);
                } else {
                  editAddress(activeCartId, body, address.id!);
                  editAdressOnFront(address.id!, addressData);
                  setIsModalOpen(false);
                }
              }}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>{footer()}</DialogActions>
    </Dialog>
  );
};

export default memo(AddressDropdown);

const FooterStack = styled(Stack)({
  flexDirection: "row",
  justifyContent: "space-between",
  width: "100%",
});
